/**
 * Reprova a identificação das faturas SELECIONADAS, uma chamada por pagamento (AF-4139).
 *
 * As outras sinalizações do pagamento ficam onde estão: a marcação parcial (AF-3671 step 4) tornou
 * a conta aberta legítima, e o pagamento volta a ter saldo a identificar.
 *
 * O motivo é obrigatório, como em toda remoção de sinalização: quem vai perguntar "por que minha
 * marcação sumiu?" é a cobrança, dias depois, olhando o histórico da FATURA.
 */
import { useState } from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { unmarkInvoices } from '../services/billingApi';
import { PaymentGroup } from '../services/paymentsFilter';
import SelectedInvoicesSummary, { payerLabel, runPerPayment } from './SelectedInvoicesSummary';

interface Props {
    groups: PaymentGroup[];
    onClose: () => void;
    onUnmarked: () => void;
}

const UnmarkInvoicesDialog: React.FC<Props> = ({ groups, onClose, onUnmarked }) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const invoices = groups.flatMap(group => group.invoices);
    const several = invoices.length > 1;

    const confirm = async () => {
        setSubmitting(true);
        const { done, failed } = await runPerPayment(groups, group => unmarkInvoices(
            group.payment.paymentId, reason.trim(), group.invoices.map(inv => inv.invoiceId)));
        setSubmitting(false);
        failed.forEach(({ group, message }) => toast.error(
            `Não foi possível reprovar o recebimento de ${payerLabel(group.payment)}: ${message}`));
        if (done.length === 0) return;
        toast.success(failed.length === 0
            ? (several
                ? `Sinalização removida de ${invoices.length} faturas.`
                : 'Sinalização removida desta fatura.')
            : `Sinalização removida no recebimento de ${done.map(g => payerLabel(g.payment)).join(', ')}.`);
        onUnmarked();
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div role="dialog" aria-label="Reprovar identificação"
                className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <FaExclamationTriangle className="text-amber-500" />
                        {several ? `Reprovar ${invoices.length} faturas` : 'Reprovar esta fatura'}
                    </h3>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="px-6 py-4 space-y-3">
                    <SelectedInvoicesSummary groups={groups} />

                    <p className="text-sm px-3 py-2 rounded bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        Só {several ? 'estas faturas perdem' : 'esta fatura perde'} a sinalização — as
                        outras {groups.length > 1 ? 'destes pagamentos' : 'deste pagamento'} continuam
                        sinalizadas. O valor volta a ficar disponível para identificar.
                    </p>

                    <div>
                        <label className="block text-sm font-medium mb-1" htmlFor="motivo-reprovacao-faturas">
                            Motivo da reprovação *
                        </label>
                        <textarea
                            id="motivo-reprovacao-faturas"
                            className="w-full text-sm border rounded p-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                            rows={3}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="Por que estas faturas não são pagas por este pagamento"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Vai para o histórico de cada fatura — é o que a cobrança vai ler quando
                            procurar a sinalização que sumiu.
                        </p>
                    </div>
                </div>

                <div className="px-6 py-3 border-t dark:border-slate-600 flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    <button onClick={confirm} disabled={submitting || !reason.trim()}
                        className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                        {submitting ? 'Reprovando...' : 'Reprovar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnmarkInvoicesDialog;
